import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Loader2Icon, LucideIcon } from 'lucide-react';
import { cn } from '~/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl py-6 text-base font-light transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'border border-black text-black hover:border-transparent hover:bg-primary hover:text-white dark:border-white dark:text-white hover:dark:hover:text-black',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-black bg-background shadow-sm hover:bg-accent hover:text-accent-foreground dark:border-white',
        outlineLight:
          'border border-white bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        primary:
          'border border-primary-foreground bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:opacity-80',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-2xl px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  icon?: LucideIcon;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      icon,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Need capital letter for icon to make react happy
    const Icon = icon;

    if (isLoading) {
      return (
        <Comp
          className={cn(
            'gap-x-2',
            buttonVariants({ variant, size, className }),
          )}
          disabled={true}
          ref={ref}
          {...props}>
          <Loader2Icon className="h-4 w-4 animate-spin" />
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn('gap-x-2', buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}>
        {Icon && <Icon className="h-4 w-4" />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
